<template>
  <header class="mt-student-nav shadow-md">
    <div class="max-w-7xl mx-auto px-6 flex items-center h-16">

      <!-- LEFT: Back to Parent Dashboard -->
      <div class="flex-1 flex items-center">
        <RouterLink to="/parent/dashboard" class="parent-back-btn">
          <MTIcon name="chevron-left" :size="15" />
          <span class="parent-back-label">Parent Dashboard</span>
        </RouterLink>
      </div>

      <!-- CENTER: Nav links -->
      <nav class="hidden md:flex items-center gap-8" v-if="childId">
        <!-- <RouterLink :to="studentPath('dashboard')" class="mt-student-link">Dashboard</RouterLink> -->
        <RouterLink :to="studentPath('dashboard')" class="mt-student-link">Learn</RouterLink>
        <RouterLink :to="studentPath('track')" class="mt-student-link">Track</RouterLink>
        <RouterLink :to="studentPath('curriculum')" class="mt-student-link">Curriculum</RouterLink>
        <RouterLink :to="studentPath('worksheets')" class="mt-student-link">Worksheets</RouterLink>
      </nav>

      <!-- RIGHT: Theme picker + Avatar + Sign out -->
      <div class="flex-1 hidden md:flex items-center justify-end gap-3" v-if="childId">
        <!-- Theme Picker -->
        <div class="relative" ref="themePickerRef">
          <button
            @click="showThemePicker = !showThemePicker"
            class="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all bg-white/20 text-white hover:bg-white/30"
            title="Change theme"
          >
            <MTIcon :name="currentThemeIcon" :size="16" />
            <span>{{ currentThemeLabel }}</span>
            <MTIcon name="chevron-down" :size="12" />
          </button>
          <transition name="fade">
            <div v-if="showThemePicker" class="absolute right-0 top-full mt-2 bg-[var(--mt-card-bg)] rounded-xl shadow-lg border border-[var(--mt-border)] py-1 z-50 w-48">
              <button
                v-for="theme in themes"
                :key="theme.id"
                class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--mt-hover)] transition-colors"
                :class="preferencesStore.homeTheme === theme.id ? 'text-blue-600 font-semibold bg-blue-50' : 'text-[var(--mt-text)]'"
                @click="preferencesStore.setHomeTheme(theme.id, childId); showThemePicker = false"
              >
                <MTIcon :name="theme.icon" :size="16" />
                {{ theme.label }}
                <MTIcon v-if="preferencesStore.homeTheme === theme.id" name="check" :size="14" class="ml-auto" />
              </button>
            </div>
          </transition>
        </div>

        <!-- Avatar -->
        <div class="relative" ref="avatarPickerRef">
          <button @click="toggleAvatarPicker" class="flex items-center gap-1 transition-transform hover:scale-105">
            <MTAvatar :studentId="childId" size="sm" />
            <MTIcon name="chevron-down" :size="14" class="text-white/70" />
          </button>
          <Teleport to="body">
            <transition name="fade">
              <div
                v-if="showAvatarPicker"
                class="fixed rounded-xl shadow-lg z-50 bg-[var(--mt-card-bg)] border border-[var(--mt-border)]"
                :style="avatarPickerStyle"
              >
                <MTAvatarPicker
                  :selected="preferencesStore.getAvatar(childId)"
                  @select="onPickAvatar"
                />
              </div>
            </transition>
          </Teleport>
        </div>
      </div>

      <!-- MOBILE MENU BUTTON -->
      <button
        class="md:hidden text-white text-xl ml-auto"
        @click="isMobileMenuOpen = !isMobileMenuOpen"
      >
        ☰
      </button>
    </div>

    <!-- MOBILE MENU -->
    <div
      v-if="isMobileMenuOpen && childId"
      class="md:hidden bg-[var(--mt-header-bg)] text-[var(--mt-header-text)] border-t border-blue-700"
    >
      <nav class="flex flex-col p-4 space-y-4">
        <RouterLink :to="studentPath('dashboard')" class="mt-student-link">Learn</RouterLink>
        <RouterLink :to="studentPath('track')">Track</RouterLink>
        <RouterLink :to="studentPath('curriculum')" class="mt-student-link">Curriculum</RouterLink>
        <RouterLink :to="studentPath('worksheets')" class="mt-student-link">Worksheets</RouterLink>

        <!-- Theme Selector (Mobile) -->
        <div class="mt-4 pt-4 border-t border-white/20">
          <p class="text-xs uppercase tracking-wider text-white/60 mb-2">Theme</p>
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="theme in themes"
              :key="theme.id"
              class="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all"
              :class="preferencesStore.homeTheme === theme.id
                ? 'bg-white text-blue-700 shadow'
                : 'bg-white/10 text-white hover:bg-white/20'"
              @click="preferencesStore.setHomeTheme(theme.id, childId)"
            >
              <MTIcon :name="theme.icon" :size="16" />
              {{ theme.label }}
            </button>
          </div>
        </div>

        <button
          class="mt-4 text-sm text-white/60 hover:text-white/90 transition-colors text-left"
          @click="logout"
        >
          Sign out
        </button>
      </nav>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCurriculumStore } from "@/stores/curriculumStore";
import { usePreferencesStore } from "@/stores/preferencesStore";
import { useStudentStore } from "@/stores/studentStore";
import { useAuth } from "@/composables/useAuth";
import MTIcon from "@/components/ui/MTIcon.vue";
import MTAvatar from "@/components/ui/MTAvatar.vue";
import MTAvatarPicker from "@/components/ui/MTAvatarPicker.vue";

const route = useRoute();
const router = useRouter();
const { logout: authLogout } = useAuth();
const isMobileMenuOpen = ref(false);
const showAvatarPicker = ref(false);
const showThemePicker = ref(false);
const avatarPickerRef = ref(null);
const themePickerRef = ref(null);
const curriculumStore = useCurriculumStore();
const preferencesStore = usePreferencesStore();
const studentStore = useStudentStore();

const student = computed(() => studentStore.students.find(s => s.id === childId.value));
const studentName = computed(() => student.value?.name || "Student");

// Theme options
const themes = [
  { id: 'standard', label: 'Standard', icon: 'layout-grid' },
  { id: 'mountain-trail', label: 'Mountain Trail', icon: 'mountain' },
  { id: 'road-map', label: 'Road Map', icon: 'map' },
  { id: 'space-mission', label: 'Space Mission', icon: 'rocket' }
];

const currentThemeLabel = computed(() => themes.find(t => t.id === preferencesStore.homeTheme)?.label || 'Theme');
const currentThemeIcon = computed(() => themes.find(t => t.id === preferencesStore.homeTheme)?.icon || 'layout-grid');

/**
 * Child ID resolution strategy:
 * - Prefer route param (:childId)
 * - Fallback: query (?childId=)
 */
const childId = computed(() =>
  route.params.studentId || route.query.studentId || null
);

onMounted(async () => {
  // await curriculumStore.loadTrack();
});
/**
 * Build student-scoped paths safely
 */
const avatarPickerStyle = ref({});

function toggleAvatarPicker() {
  showAvatarPicker.value = !showAvatarPicker.value;
  if (showAvatarPicker.value) {
    const btn = avatarPickerRef.value?.querySelector('button');
    if (btn) {
      const rect = btn.getBoundingClientRect();
      avatarPickerStyle.value = {
        top: `${rect.bottom + 8}px`,
        right: `${window.innerWidth - rect.right}px`,
      };
    }
  }
}

function onPickAvatar(emoji) {
  preferencesStore.setAvatar(childId.value, emoji);
  showAvatarPicker.value = false;
}

// Close pickers on click outside
function handleClickOutside(e) {
  if (avatarPickerRef.value && !avatarPickerRef.value.contains(e.target)) {
    showAvatarPicker.value = false;
  }
  if (themePickerRef.value && !themePickerRef.value.contains(e.target)) {
    showThemePicker.value = false;
  }
}
onMounted(() => document.addEventListener('click', handleClickOutside));
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside));

async function logout() {
  await authLogout();
  studentStore.logout();
  router.push('/auth/login');
}

function studentPath(page) {
  if (!childId.value) return "/parent/dashboard";

  const map = {
    dashboard: `/student/${childId.value}/dashboard`,
    learn: `/student/${childId.value}/continue`,
    track: `/student/${childId.value}/track`,
    curriculum: `/student/${childId.value}/curriculum`,
    worksheets: `/student/${childId.value}/worksheets`,
    continue: `/student/${childId.value}/continue`,
    profile: `/student/${childId.value}/profile`,
  };

  return map[page] || `/student/${childId.value}/dashboard`;
}
</script>

<style>
@reference "tailwindcss";

/* Wrapper */
.mt-student-nav {
  background: var(--mt-header-bg);
  color: var(--mt-header-text);
}

/* Parent back button */
.parent-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px 5px 8px;
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.2);
  color: rgba(255,255,255,0.75);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.15s;
  background: rgba(255,255,255,0.08);
  white-space: nowrap;
}
.parent-back-btn:hover {
  background: rgba(255,255,255,0.16);
  border-color: rgba(255,255,255,0.35);
  color: white;
}
.parent-back-label {
  display: inline;
}
@media (max-width: 480px) {
  .parent-back-label {
    display: none;
  }
  .parent-back-btn {
    padding: 5px 8px;
  }
}

/* Desktop links */
.mt-student-link {
  @apply text-sm font-medium text-white hover:opacity-80 transition;
}

/* Mobile links */
.mt-student-mobile-link {
  @apply text-base font-medium text-white hover:opacity-70 transition py-1;
}
</style>
